import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { animateCoins, animateGems } from '../../animation/CurrencyAnimation';
import { Feature } from 'src/replicant/analytics';
import gcinstant from '@play-co/gcinstant';

export default class PopupInstallNativeReward extends PopupBasic {
  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: uiConfig.popups.minimumWidth,
      height: 510,
      tracking: {
        feature: Feature.NATIVE._,
        $subFeature:
          gcinstant.osType === 'IOS'
            ? Feature.NATIVE.POPUP_INSTALL_REWARD_IOS
            : gcinstant.osType === 'ANDROID'
            ? Feature.NATIVE.POPUP_INSTALL_REWARD_ANDROID
            : Feature.NATIVE.POPUP_INSTALL_REWARD_UNKNOWN,
      },
    });

    this.title.updateOpts({ size: 36 });
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      width: this.box.style.width - 80,
      height: 80,
      x: 40,
      y: 68,
      zIndex: 1,
    });

    this.box.addSubview(
      new ImageScaleView({
        height: 5,
        width: this.message.style.width,
        x: 40,
        y: 132,
        image: 'assets/ui/popups/panel_underline.png',
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 5, right: 5 },
        },
      }),
    );

    const reward = {
      coins: 1000,
      gems: 100,
    };

    const gemsReward = this.createRewardView(this.box, {
      y: 228,
      type: 'gems',
      value: reward.gems,
    });

    const coinsReward = this.createRewardView(this.box, {
      y: 328,
      type: 'coins',
      value: reward.coins,
    });

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...uiConfig.buttons.secondary,
        labelOffsetX: -4,
        labelOffsetY: -1,
        fontSize: 38,
        font: bitmapFonts('Body'),
        x: this.box.style.width / 2,
        y: this.box.style.height - 35 - 80 / 2,
        width: 229,
        height: 80,
        centerOnOrigin: true,
        localeText: () => i18n('basic.ok'),
        onClick: async () => {
          sounds.playSound('ok');

          await Promise.all([coinsReward.animate(), gemsReward.animate()]);

          opts.close();
        },
        tracking: {
          feature: Feature.NATIVE._,
          $subFeature: Feature.NATIVE.POPUP_INSTALL_CLOSE,
        },
      }),
    );
  }

  /**
   * Create reward view
   */
  private createRewardView(
    superview: View,
    opts: { y: number; value: number; type: 'coins' | 'gems' },
  ) {
    const reward = new ImageSliceView({
      superview,
      image: 'assets/ui/shared/lossless/inset_panel.png',
      slices: {
        left: 25,
        right: 25,
        top: 25,
        bot: 25,
      },
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: opts.y,
      height: 88,
      width: 300,
    });

    let icon: ImageScaleView;
    if (opts.type === 'coins') {
      icon = new ImageScaleView({
        superview: reward,
        scaleMethod: 'contain',
        image: 'assets/ui/hud/icons/hud_coin.png',
        width: 74,
        height: 74,
        x: 10,
        y: 7,
      });
    } else {
      icon = new ImageScaleView({
        superview: reward,
        scaleMethod: 'contain',
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 50,
        height: 42,
        scale: 1.7,
        x: 3,
        y: 11,
      });
    }

    const label = new LangBitmapFontTextView({
      superview: reward,
      size: 38,
      font: 'Body',
      height: 68,
      width: 196,
      verticalAlign: 'center',
      align: 'center',
      localeText: () => `x ${parseAmount(opts.value)}`,
      x: 90,
      y: 8,
    });

    const animate = async () => {
      const iconPos = icon.getPosition(this.root);
      const start = {
        x: iconPos.x + icon.style.width / 2,
        y: iconPos.y + icon.style.height / 2,
      };

      if (opts.type === 'coins') {
        await animateCoins({
          superview: this.root,
          start: { position: start, scale: 1 },
          count: opts.value,
        });
      } else {
        await animateGems({
          superview: this.root,
          start: { position: start, scale: 1 },
          count: opts.value,
        });
      }
    };

    return { label, animate };
  }

  public init(opts: {}) {
    super.init({
      title: i18n('native.install.reward.title'),
      message: i18n('native.install.reward.message'),
    });

    sounds.playSound('applause');
  }
}
